export enum AlertType {
  // Patient related alerts
  PATIENT_CREATED = "PATIENT_CREATED",
  PATIENT_UPDATED = "PATIENT_UPDATED",

  // Appointment and scheduling alerts
  APPOINTMENT_CANCELLED = "APPOINTMENT_CANCELLED",
  APPOINTMENT_UPDATED = "APPOINTMENT_UPDATED",
  APPOINTMENT_REQUEST = "APPOINTMENT_REQUEST",
  WAITLIST_ADDED = "WAITLIST_ADDED",
  APPOINTMENT_SCHEDULED = "APPOINTMENT_SCHEDULED",

  SELF_SCHEDULED_APPOINTMENT = "SELF_SCHEDULED_APPOINTMENT",
  SELF_SCHEDULED_APPOINTMENT_CANCELLED = "SELF_SCHEDULED_APPOINTMENT_CANCELLED",
  SELF_SCHEDULED_APPOINTMENT_UPDATED = "SELF_SCHEDULED_APPOINTMENT_UPDATED",

  BLOCK_CREATED = "BLOCK_CREATED",
  BLOCK_CANCELLED = "BLOCK_CANCELLED",
  BLOCK_UPDATED = "BLOCK_UPDATED",

  SHIFT_CREATED = "SHIFT_CREATED",
  SHIFT_CANCELLED = "SHIFT_CANCELLED",
  SHIFT_UPDATED = "SHIFT_UPDATED",

  // Pubsub-only — emitted by the gcal incremental sync. Drives surgical
  // cache mutations only; never raises a provider notification.
  GCAL_EVENT_CREATED = "GCAL_EVENT_CREATED",
  GCAL_EVENT_UPDATED = "GCAL_EVENT_UPDATED",
  GCAL_EVENT_CANCELLED = "GCAL_EVENT_CANCELLED",

  // Payment and billing alerts
  PAYMENT_CREATED = "PAYMENT_CREATED",
  PAYMENT_FAILED = "PAYMENT_FAILED",
  PAYMENT_SUCCEEDED = "PAYMENT_SUCCEEDED",
  // A card or bank payment recorded at processing; shown as "Payment Made".
  PAYMENT_PROCESSING = "PAYMENT_PROCESSING",
  PLANNED_PAYMENT_FAILED = "PLANNED_PAYMENT_FAILED",
  PLANNED_PAYMENT_SUCCEEDED = "PLANNED_PAYMENT_SUCCEEDED",
  MEMBERSHIP_DELINQUENT = "MEMBERSHIP_DELINQUENT",
  // Membership lifecycle events for integrations (webhooks). Never a
  // notification — see NON_NOTIFIABLE_ALERT_TYPES on the backend.
  MEMBERSHIP_CREATED = "MEMBERSHIP_CREATED",
  MEMBERSHIP_CANCELLED = "MEMBERSHIP_CANCELLED",
  MEMBERSHIP_FROZEN = "MEMBERSHIP_FROZEN",
  CHARGE_CREATED = "CHARGE_CREATED",
  REFUND_CREATED = "REFUND_CREATED",
  REFUND_FAILED = "REFUND_FAILED",
  REFUND_SUCCEEDED = "REFUND_SUCCEEDED",
  ADJUSTMENT_CREATED = "ADJUSTMENT_CREATED",
  ADJUSTMENT_FAILED = "ADJUSTMENT_FAILED",
  ADJUSTMENT_SUCCEEDED = "ADJUSTMENT_SUCCEEDED",
  INVOICE_UPDATED = "INVOICE_UPDATED",
  INVOICE_STATUS_UPDATE = "INVOICE_STATUS_UPDATE",
  INVOICE_SET_UPDATED = "INVOICE_SET_UPDATED",
  CHARGE_PAYMENT_CREATED = "CHARGE_PAYMENT_CREATED",
  // Historical event name for patient quote acceptance (now charge-backed).
  // Persisted notification; eligible for opt-in task auto-creation.
  CART_ACCEPTED = "CART_ACCEPTED",
  // Raised when staff close the cash drawer for a location.
  CASH_REGISTER_CLOSED = "CASH_REGISTER_CLOSED",
  // Raised once when a patient's online-store order is paid for.
  SHOP_PURCHASE = "SHOP_PURCHASE",

  // Communication alerts
  CALL_RECEIVED = "CALL_RECEIVED",
  CALL_ANSWERED = "CALL_ANSWERED",
  VOICEMAIL_RECEIVED = "VOICEMAIL_RECEIVED",
  CALL_MADE = "CALL_MADE",
  // Pubsub-only desk-phone screen-pops. Targeted to the desk phone's
  // owner / location-mates / online users; dismiss clears for everyone.
  DESK_PHONE_SCREEN_POP = "DESK_PHONE_SCREEN_POP",
  DESK_PHONE_SCREEN_POP_DISMISSED = "DESK_PHONE_SCREEN_POP_DISMISSED",
  MESSAGE_RECEIVED = "MESSAGE_RECEIVED",
  MESSAGE_SENT = "MESSAGE_SENT",
  MESSAGE_DELIVERED = "MESSAGE_DELIVERED",
  MESSAGE_FAILED = "MESSAGE_FAILED",
  EMAIL_RECEIVED = "EMAIL_RECEIVED",
  EMAIL_REPLY_RECEIVED = "EMAIL_REPLY_RECEIVED",
  EMAIL_SENT = "EMAIL_SENT",
  EMAIL_INVOICE_SENT = "EMAIL_INVOICE_SENT",
  MAIL_SENT = "MAIL_SENT",
  MAIL_DELIVERY_FAILED = "MAIL_DELIVERY_FAILED",
  // Fax delivery — pubsub-only, terminal-state transitions only.
  FAX_DELIVERED = "FAX_DELIVERED",
  FAX_FAILED = "FAX_FAILED",
  // Inbound fax — persisted, routed by messaging:fax:read.
  FAX_RECEIVED = "FAX_RECEIVED",
  // Pubsub-only — broadcasts a fax row's read-state flip for cache patching.
  FAX_UPDATE = "FAX_UPDATE",
  // Pubsub-only — fires when an inbound fax's AI analysis reaches a terminal
  // state, so the suggestions strip fills in without polling. Carries {id}.
  FAX_ANALYSIS_UPDATE = "FAX_ANALYSIS_UPDATE",
  // Pubsub-only — the Mailgun validation-job webhook fires this once a bulk
  // email-verification job is ingested (or fails), so the blast "Verify emails"
  // bar refreshes its summary without waiting on its poll.
  EMAIL_VERIFICATION_COMPLETE = "EMAIL_VERIFICATION_COMPLETE",

  // Form and notification alerts
  FORM_SUBMITTED = "FORM_SUBMITTED",
  NOTIFICATION = "NOTIFICATION",
  APPOINTMENT_CHECKLIST_FILLED_FORM = "APPOINTMENT_CHECKLIST_FILLED_FORM",
  APPOINTMENT_CHECKLIST_CHECKED_IN = "APPOINTMENT_CHECKLIST_CHECKED_IN",
  APPOINTMENT_CHECKLIST_WITH_MA = "APPOINTMENT_CHECKLIST_WITH_MA",
  APPOINTMENT_CHECKLIST_MEASUREMENT_TAKEN = "APPOINTMENT_CHECKLIST_MEASUREMENT_TAKEN",
  APPOINTMENT_CHECKLIST_DOSE_TAKEN = "APPOINTMENT_CHECKLIST_DOSE_TAKEN",
  APPOINTMENT_CHECKLIST_WITH_PROVIDER = "APPOINTMENT_CHECKLIST_WITH_PROVIDER",
  APPOINTMENT_CHECKLIST_NOTE_TAKEN = "APPOINTMENT_CHECKLIST_NOTE_TAKEN",
  APPOINTMENT_CHECKLIST_PAYMENT_MADE = "APPOINTMENT_CHECKLIST_PAYMENT_MADE",
  APPOINTMENT_CHECKLIST_REQUEST_GFE = "APPOINTMENT_CHECKLIST_REQUEST_GFE",
  APPOINTMENT_CHECKLIST_PICTURE_TAKEN = "APPOINTMENT_CHECKLIST_PICTURE_TAKEN",
  APPOINTMENT_CHECKLIST_CARE_PLAN = "APPOINTMENT_CHECKLIST_CARE_PLAN",
  APPOINTMENT_CHECKLIST_NOTE_SIGNED_OFF = "APPOINTMENT_CHECKLIST_NOTE_SIGNED_OFF",
  APPOINTMENT_CHECKLIST_REBOOK = "APPOINTMENT_CHECKLIST_REBOOK",
  APPOINTMENT_CHECKLIST_PROFILE_PHOTO = "APPOINTMENT_CHECKLIST_PROFILE_PHOTO",
  // Pubsub-only: a step was unticked, so open screens clear it.
  APPOINTMENT_CHECKLIST_REOPENED = "APPOINTMENT_CHECKLIST_REOPENED",
  LOW_PATIENT_REVIEW = "LOW_PATIENT_REVIEW",

  // Good Faith Exam alerts — a completed Spakinect GFE deferred (declined) treatments.
  GFE_TREATMENTS_DEFERRED = "GFE_TREATMENTS_DEFERRED",

  // Note alerts
  NOTE_CREATED = "NOTE_CREATED",
  NOTE_UPDATED = "NOTE_UPDATED",

  // Note-template catalog mutations — pubsub-only. One event covers create,
  // update and archive; the payload is the whole template row as the list
  // endpoint serializes it (minus the per-user `isFavorited`) plus `isArchived`,
  // so useNoteTemplateRealtimeCache upserts or drops the cached row without a
  // refetch.
  NOTE_TEMPLATE_UPDATED = "NOTE_TEMPLATE_UPDATED",
  FOLLOW_UP_UPDATED = "FOLLOW_UP_UPDATED",
  WAITLIST_UPDATED = "WAITLIST_UPDATED",
  CALL_TRANSCRIBED = "CALL_TRANSCRIBED",

  // Telehealth alerts (ENG-2491) — pubsub-only, drives the bot-status pill
  TELEHEALTH_BOT_STATUS = "TELEHEALTH_BOT_STATUS",

  // Social messaging alerts
  COMMUNICATION_MESSAGE_RECEIVED = "COMMUNICATION_MESSAGE_RECEIVED",
  COMMUNICATION_MESSAGE_SENT = "COMMUNICATION_MESSAGE_SENT",
  COMMUNICATION_CONVERSATION_UPDATED = "COMMUNICATION_CONVERSATION_UPDATED",

  // Lab orders — pubsub-only. Junction webhooks fan out via this single event
  // so the labs tab refreshes in real time. Payload is shaped as
  // { patient_id, lab_order_id, internal_status, vendor_status }.
  LAB_ORDER_UPDATED = "LAB_ORDER_UPDATED",

  // Persisted (unlike LAB_ORDER_UPDATED) and gated on labs:read.
  LAB_RESULTS_READY = "LAB_RESULTS_READY",

  // Fullscript supplements — pubsub-only. Fullscript webhooks (treatment plan
  // created/updated, order placed) fan out via this single event so the
  // supplements tab refreshes in real time. Payload carries ids only
  // ({ patient_id, treatment_plan_id? }); the cache invalidate refetches the
  // cheap local-DB list.
  FULLSCRIPT_TREATMENT_PLAN_UPDATED = "FULLSCRIPT_TREATMENT_PLAN_UPDATED",

  // Team chat alerts
  TEAM_MESSAGE_RECEIVED = "TEAM_MESSAGE_RECEIVED",
  TEAM_CONVERSATION_UPDATED = "TEAM_CONVERSATION_UPDATED",
  TEAM_TYPING_INDICATOR = "TEAM_TYPING_INDICATOR",
  TEAM_MESSAGE_EDITED = "TEAM_MESSAGE_EDITED",
  TEAM_PRESENCE_UPDATE = "TEAM_PRESENCE_UPDATE",
  TEAM_MESSAGE_REACTION = "TEAM_MESSAGE_REACTION",
  TEAM_READ_RECEIPT = "TEAM_READ_RECEIPT",

  // Alert types primarily used for front end updates (not shown as notifications)
  CHAT_UPDATE = "CHAT_UPDATE",
  RAINFOREST_PAYIN_FAILED = "RAINFOREST_PAYIN_FAILED",
  RAINFOREST_PAYIN_PROCESSING = "RAINFOREST_PAYIN_PROCESSING",
  RAINFOREST_POS_PAYIN_CANCELLED = "RAINFOREST_POS_PAYIN_CANCELLED",

  // Deposit alerts
  RAINFOREST_DEPOSIT_IN_REVIEW = "RAINFOREST_DEPOSIT_IN_REVIEW",
  RAINFOREST_DEPOSIT_SUCCEEDED = "RAINFOREST_DEPOSIT_SUCCEEDED",
  RAINFOREST_DEPOSIT_FAILED = "RAINFOREST_DEPOSIT_FAILED",

  // ACH return alerts
  ACH_RETURN = "ACH_RETURN",

  // Chargeback alerts
  CHARGEBACK_ACTION_REQUIRED = "CHARGEBACK_ACTION_REQUIRED",
  CHARGEBACK_UPDATED = "CHARGEBACK_UPDATED",
  CHARGEBACK_WON = "CHARGEBACK_WON",
  CHARGEBACK_LOST = "CHARGEBACK_LOST",

  // Inventory alerts
  STOCK_LOW = "STOCK_LOW",
  STOCK_ADDED = "STOCK_ADDED",
  STOCK_ARCHIVED = "STOCK_ARCHIVED",
  STOCK_LINKED_TO_ITEM = "STOCK_LINKED_TO_ITEM",
  STOCK_UNLINKED_FROM_ITEM = "STOCK_UNLINKED_FROM_ITEM",
  STOCK_UPDATED = "STOCK_UPDATED",
  STOCK_DRAWDOWN = "STOCK_DRAWDOWN",
  STOCK_PARTIAL_DRAWDOWN = "STOCK_PARTIAL_DRAWDOWN",
  PROVIDER_ALLOCATION_LOW = "PROVIDER_ALLOCATION_LOW",
  // Stock at a Coupa clinic newly stopped matching Coupa; raised at its HQ, or the clinic itself.
  COUPA_DRIFT = "COUPA_DRIFT",
  SHIPMENT_RECEIVED = "SHIPMENT_RECEIVED",
  SHIPMENT_UPDATED = "SHIPMENT_UPDATED",
  SHIPMENT_ARCHIVED = "SHIPMENT_ARCHIVED",
  COUPA_ORDER_OVERDUE = "COUPA_ORDER_OVERDUE",
  ITEM_CREATED = "ITEM_CREATED",
  ITEM_UPDATED = "ITEM_UPDATED",
  ITEM_ARCHIVED = "ITEM_ARCHIVED",
  SUPPLIER_LINKED_TO_STOCK = "SUPPLIER_LINKED_TO_STOCK",
  SUPPLIER_UNLINKED_FROM_STOCK = "SUPPLIER_UNLINKED_FROM_STOCK",
  SUPPLIER_TO_STOCK_UPDATED = "SUPPLIER_TO_STOCK_UPDATED",
  PO_CREATED = "PO_CREATED",
  PO_PLACED = "PO_PLACED",
  PO_CANCELLED = "PO_CANCELLED",
  PO_COMPLETED = "PO_COMPLETED",
  PO_ARCHIVED = "PO_ARCHIVED",

  // Inventory actions
  INVENTORY_ACTION = "INVENTORY_ACTION",

  // Task alerts
  TASK_ASSIGNED = "TASK_ASSIGNED",
  TASK_COMMENT = "TASK_COMMENT",
  TASK_COMPLETED = "TASK_COMPLETED",
  TASK_UPDATED = "TASK_UPDATED",

  // Settings approvals — sent to the requester when their change is approved or declined
  APPROVAL_REQUEST_REVIEWED = "APPROVAL_REQUEST_REVIEWED",
  // Sent to the requester when their export request is approved or declined
  EXPORT_REQUEST_REVIEWED = "EXPORT_REQUEST_REVIEWED",

  // Growth (CRM opportunity) alerts
  OPPORTUNITY_CREATED = "OPPORTUNITY_CREATED",
  OPPORTUNITY_STAGE_CHANGED = "OPPORTUNITY_STAGE_CHANGED",
  OPPORTUNITY_WON = "OPPORTUNITY_WON",
  OPPORTUNITY_LOST = "OPPORTUNITY_LOST",
  OPPORTUNITY_ASSIGNED = "OPPORTUNITY_ASSIGNED",
  OPPORTUNITY_NOTE_ADDED = "OPPORTUNITY_NOTE_ADDED",
  LEAD_FORM_SUBMITTED = "LEAD_FORM_SUBMITTED",
}
